// Раздел пульта в дереве маршрутов (задача D4): у маршрутов раздела в `handle` лежит его строка из `ROUTES`.
// По ней `SectionGate` пускает в раздел или показывает «Нет доступа», а заглушка и каркас (D5) берут название.
// Роль читается на каждом показе: вошёл другой человек через окно «Сессия истекла» — экран сразу перерисуется.
import type { Role } from '@contract/index.js';
import type { LucideIcon } from 'lucide-react';
import { Outlet, useMatches } from 'react-router';
import { useRole } from '@/api/queries/auth';
import { Forbidden } from './forbidden';
import type { ScreenId } from './routes';

export interface SectionInfo {
  readonly titleRu: string;
  readonly icon: LucideIcon;
  /** Кому раздел открыт. */
  readonly roles: readonly Role[];
}

export interface SectionHandle {
  readonly section: SectionInfo;
  /** Экран раздела (у маршрутов экранов, D5): по нему шпаргалка «?» показывает клавиши этого экрана. */
  readonly screen?: ScreenId;
}

function isSectionHandle(handle: unknown): handle is SectionHandle {
  return typeof handle === 'object' && handle !== null && 'section' in handle;
}

/** Раздел, в котором открыт экран: ближайший к экрану маршрут с разделом в `handle`. */
export function useSection(): SectionInfo | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle: unknown = matches[i]?.handle;
    if (isSectionHandle(handle)) return handle.section;
  }
  return undefined;
}

/** Открытый экран (`readiness`, `dialog`…); вне экранов раздела — `undefined`. */
export function useScreen(): ScreenId | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle: unknown = matches[i]?.handle;
    if (isSectionHandle(handle) && handle.screen !== undefined) return handle.screen;
  }
  return undefined;
}

/** Вход в раздел: роль подходит — экран раздела, нет — «Нет доступа» с ролью и тем, кто даёт доступ. */
export function SectionGate() {
  const section = useSection();
  const role = useRole();
  if (section === undefined || role === undefined) return null;
  if (!section.roles.includes(role)) {
    return <Forbidden titleRu={section.titleRu} role={role} allowed={section.roles} />;
  }
  return <Outlet />;
}
